import { Action } from '../actions/ActionInterface';
import { ActionTypes } from '../actions/ActionTypes';

export interface UserInteractionsState {
  favorites: string[];
  tastingList: string[];
  triedList: string[];
  ratings: Record<string, number>;
  notes: Record<string, string>;
}

const initialState: UserInteractionsState = {
  favorites: [],
  tastingList: [],
  triedList: [],
  ratings: {},
  notes: {},
};

/**
 * User interactions reducer
 * Handles all actions related to user interactions with beers
 * Following Single Responsibility Principle by focusing only on user interactions
 */
export function userInteractionsReducer(state: UserInteractionsState = initialState, action: Action): UserInteractionsState {
  // Use the action's execute method if available (Command pattern)
  // Check if execute exists without causing TypeScript errors
  if (action && typeof (action as any).execute === 'function') {
    // The execute method expects the full state, so we need to adapt it
    const fullState = { userInteractions: state } as any;
    const newState = action.execute(fullState);
    
    // Extract only the userInteractions property this reducer is responsible for
    return newState.userInteractions;
  }
  
  // Fallback to traditional switch statement for backward compatibility
  switch (action.type) {
    case ActionTypes.TOGGLE_FAVORITE: {
      const beerId = action.payload;
      
      if (!beerId) {
        return state;
      }
      
      const favorites = [...state.favorites];
      const index = favorites.indexOf(beerId);
      
      if (index === -1) {
        favorites.push(beerId);
      } else {
        favorites.splice(index, 1);
      }
      
      return {
        ...state,
        favorites,
      };
    }
      
    case ActionTypes.UPDATE_RATING: {
      const { beerId, rating } = action.payload || { beerId: '', rating: 0 };
      
      if (!beerId) {
        return state;
      }
      
      return {
        ...state,
        ratings: {
          ...state.ratings,
          [beerId]: rating,
        },
      };
    }
      
    case ActionTypes.UPDATE_NOTES: {
      const { beerId, notes } = action.payload || { beerId: '', notes: '' };
      
      if (!beerId) {
        return state;
      }
      
      return {
        ...state,
        notes: {
          ...state.notes,
          [beerId]: notes,
        },
      };
    }
      
    case ActionTypes.TOGGLE_TASTING_LIST: {
      const beerId = action.payload;
      
      if (!beerId) {
        return state;
      }
      
      const tastingList = [...state.tastingList];
      const index = tastingList.indexOf(beerId);
      
      if (index === -1) {
        tastingList.push(beerId);
      } else {
        tastingList.splice(index, 1);
      }
      
      return {
        ...state,
        tastingList,
      };
    }
    
    case ActionTypes.TOGGLE_TRIED: {
      const beerId = action.payload;
      
      if (!beerId) {
        return state;
      }
      
      const triedList = [...(state.triedList || [])];
      const index = triedList.indexOf(beerId);
      
      if (index === -1) {
        triedList.push(beerId);
      } else {
        triedList.splice(index, 1);
      }
      
      return {
        ...state,
        triedList,
      };
    }
      
    case ActionTypes.SET_USER_INTERACTIONS: {
      const userInteractions = action.payload;
      
      if (!userInteractions) {
        return state;
      }
      
      return {
        ...state,
        ...userInteractions,
      };
    }
      
    default:
      return state;
  }
}
